import React, { useState } from 'react';
import FormModal from 'v2/common/FormModal/FormModal';
import { ACLUSer, DjangoListResponse, User } from 'v2/models';
import PermissionsForm, { PermissionsChangesField } from './PermissionsForm';
import { useForm } from 'react-hook-form';
import classnames from 'classnames';
import styles from './reviewPermissionsModal.module.scss';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import {
  GeneralSelect,
  OptionType,
  SingleValue,
} from 'v2/form/CustomSelectors/GeneralSelect';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getTitle } from 'v2/utils/userUtils';
import { infiniteStrapiUsersKeys } from 'v2/queries';

interface ReviewPermissionsModalProps {
  onClose?: () => void;
  member: User;
}

const ReviewPermissionsModal = ({
  onClose,
  member,
}: ReviewPermissionsModalProps) => {
  const queryClient = useQueryClient();
  const formContext = useForm<PermissionsChangesField>({
    mode: 'onChange',
  });

  const options: OptionType[] = [
    { label: 'Data Relationship Manager', value: 'drm' },
  ];

  const [selectedApp, setSelectedApp] = useState<SingleValue<OptionType>>(
    options[0]
  );

  const {
    data: accessControls,
    isError: isAccessControlsError,
    refetch: accessControlsRefetch,
  } = useQuery(
    ['Get permissions by member', member.id],
    async () => {
      const response = await restFactory.get<DjangoListResponse<ACLUSer>>(
        `/dj/api/v1/access_control_user/`,
        {
          strapi_user: member.id,
          content_type__app_label: selectedApp?.value,
          size: 500,
        }
      );
      return response.results;
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const {
    mutate: updateAccessControls,
    isLoading: isUpdateAccessControlsLoading,
  } = useMutation(
    async (data: PermissionsChangesField) => {
      const accessControlsPayload = Object.values(data.accessControls).flatMap(
        (action) => Object.values(action)
      );

      const cleanedData = {
        access_controls: accessControlsPayload,
        user_id: member.id,
      };

      return await restFactory.patch(
        `/dj/api/v1/access_control_user/list_update_access_controls/`,
        cleanedData
      );
    },
    {
      onSuccess: () => {
        onClose?.();
        toast.success('Permissions updated.');
      },
      onError: () => {
        accessControlsRefetch();
        toast.error('Error updating permissions.');
      },
      onSettled: () => {
        queryClient.invalidateQueries(infiniteStrapiUsersKeys.all);
      },
    }
  );

  const handleOnSubmit = (data: PermissionsChangesField) => {
    updateAccessControls(data);
  };

  const handleChangeApp = (selectedOption: SingleValue<OptionType>) => {
    setSelectedApp(selectedOption);
  };

  const render = () => {
    if (isAccessControlsError) {
      return <div className="p-5">Try again later.</div>;
    }
    if (!accessControls) {
      return (
        <div className="p-5">
          <div className="loadingIndicatorContainer">
            <Spinner role="progressbar" animation="border" />
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="flex d-flex justify-content-between align-items-center">
          <span className={styles.description}>
            Please set access levels to the entities below.
          </span>

          <GeneralSelect
            options={options}
            handleChange={handleChangeApp}
            selectedOption={selectedApp}
            fullWidth
          />
        </div>
        <PermissionsForm accessControls={accessControls} />
      </>
    );
  };

  return (
    <FormModal
      title={getTitle(member)}
      shouldResetForm={false}
      onClose={() => onClose?.()}
      formContext={formContext}
      className={classnames(styles.viewModals)}
      onSubmit={handleOnSubmit}
      isLoading={isUpdateAccessControlsLoading}
    >
      {render()}
    </FormModal>
  );
};

export default ReviewPermissionsModal;
